// This service centralizes all API interactions.
import { parseAttachmentUrls } from '../utils/helpers';

const API_URL = 'https://api.sachblogmeo.com/api';
const LOGIN_PASSWORD = 'Sachblogmeo@123';
const API_SECRET_KEY = 'Sachblogmeo@123';

const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'x-api-key': localStorage.getItem('sbm_api_key') || ''
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

async function request(endpoint, method = 'GET', body = null, isFormData = false) {
    const response = await fetch(`${API_URL}/${endpoint}`, {
        method,
        headers: getAuthHeaders(isFormData),
        body: isFormData ? body : (body ? JSON.stringify(body) : null)
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error("401 Unauthorized");
        }
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `API request to ${endpoint} failed.`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json();
    }
    return response.text();
}

export const apiService = {
    login: (password) => {
        if (password === LOGIN_PASSWORD) {
            localStorage.setItem('sbm_api_key', API_SECRET_KEY);
            return true;
        }
        return false;
    },
    logout: () => localStorage.removeItem('sbm_api_key'),
    checkAuth: () => localStorage.getItem('sbm_api_key') === API_SECRET_KEY,
    
    fetchAll: async () => {
        const endpoints = ['orders', 'accounts', 'transactions', 'liabilities', 'archives'];
        const [ordersData, accountsData, transactionsData, liabilitiesData, archivesData] = await Promise.all(
            endpoints.map(ep => request(ep))
        );
        return {
            orders: ordersData.map(o => ({...o, items: typeof o.items === 'string' ? JSON.parse(o.items) : o.items || []})),
            accounts: accountsData,
            transactions: transactionsData,
            liabilities: liabilitiesData,
            archives: archivesData
        };
    },

    // --- Order APIs ---
    saveOrder: (data, isEditing) => {
        const endpoint = isEditing ? `orders/${data.id}` : 'orders';
        const method = isEditing ? 'PUT' : 'POST';
        return request(endpoint, method, data);
    },
    deleteOrder: (id) => request(`orders/${id}`, 'DELETE'),

    // --- Account APIs ---
    saveAccount: (data, isEditing) => {
        const endpoint = isEditing ? `accounts/${data.id}` : 'accounts';
        const method = isEditing ? 'PUT' : 'POST';
        return request(endpoint, method, data);
    },

    // --- File Upload API ---
    uploadFile: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const result = await request('upload', 'POST', formData, true);
        return result.url;
    },
    
    // --- Transaction APIs ---
    saveTransaction: async (data, files, isEditing) => {
        let uploadedUrls = [];
        if (files && files.length > 0) {
            uploadedUrls = await Promise.all(files.map(file => apiService.uploadFile(file)));
        }
        
        const payload = { ...data };
        const existingUrls = parseAttachmentUrls(payload.attachmentUrl);
        
        payload.attachmentUrls = [...existingUrls, ...uploadedUrls];
        delete payload.attachmentUrl;
        
        if(!isEditing) {
            payload.id = `${payload.type.slice(0, 1)}P-${Date.now()}`;
        }
        
        const endpoint = isEditing ? `transactions/${payload.id}` : 'transactions';
        const method = isEditing ? 'PUT' : 'POST';
        return request(endpoint, method, payload);
    },
    deleteTransaction: (id) => request(`transactions/${id}`, 'DELETE'),
    
    // --- Liability APIs ---
    saveLiability: async (data, file, isEditing) => {
        const payload = {...data};
        if (file) {
            payload.attachmentUrl = await apiService.uploadFile(file);
        }
        const endpoint = isEditing ? `liabilities/${payload.id}` : 'liabilities';
        const method = isEditing ? 'PUT' : 'POST';
        return request(endpoint, method, payload);
    },
    deleteLiability: (id) => request(`liabilities/${id}`, 'DELETE'),
    updateLiabilityStatus: (id, status) => request(`liabilities/${id}/status`, 'PUT', { status }),

    // --- Archive APIs ---
    saveArchive: async (file, date) => {
        const url = await apiService.uploadFile(file);
        const payload = { file_name: file.name, file_url: url, storage_date: date };
        return request('archives', 'POST', payload);
    },
    deleteArchive: (id) => request(`archives/${id}`, 'DELETE'),
};
